import { useCallback, useEffect } from 'react'
import { computeGuideSnappedMoveDelta, computeSnappedNudgeItems } from '../gestures/guide-snapping'
import type { InteractionState } from '../types/interaction-state'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { EditScopeRefsApi } from './use-edit-scope-refs'
import type { SmartGuidesApi } from './use-smart-guides'

export function useGestureSnapping(ctx: ShapeManagerStateApi & EditScopeRefsApi & SmartGuidesApi) {
  const {
    editScopeGroupIdRef,
    getMovedSelectionBounds,
    getSnappedNudgeItemsRef,
    gridSnapStep,
    guides,
    resolvedGridSnapLiveRects,
    selectedShapeIdsRef,
    shapesRef,
    snapGuideDistance,
    snapToGrid,
    snapToGuides,
  } = ctx

  const getGuideSnappedMoveDelta = useCallback(
    (
      rawDx: number,
      rawDy: number,
      interaction: Pick<InteractionState, 'shapeId' | 'initial' | 'multiMoveInitials'>,
      /** When false, skip grid snap (fluid drag). When true, grid runs after ruler-guide snap so commit matches drag preview. */
      options?: { applyGridSnap?: boolean }
    ): { dx: number; dy: number } =>
      computeGuideSnappedMoveDelta(rawDx, rawDy, interaction, options, {
        getShapes: () => shapesRef.current,
        guides,
        snapToGuides,
        snapGuideDistance,
        snapToGrid,
        gridSnapStep,
        gridSnapLiveRects: resolvedGridSnapLiveRects,
        getMovedSelectionBounds,
      }),
    [
      guides,
      getMovedSelectionBounds,
      snapGuideDistance,
      snapToGuides,
      snapToGrid,
      gridSnapStep,
      resolvedGridSnapLiveRects,
      shapesRef,
    ]
  )

  const getSnappedNudgeItems = useCallback(
    (
      rawDx: number,
      rawDy: number
    ): Array<{
      shapeId: string
      before: { x: number; y: number }
      after: { x: number; y: number }
    }> =>
      computeSnappedNudgeItems(rawDx, rawDy, {
        shapes: shapesRef.current,
        selectedIds: selectedShapeIdsRef.current,
        editScopeGroupId: editScopeGroupIdRef.current,
      }),
    [editScopeGroupIdRef, selectedShapeIdsRef, shapesRef]
  )

  useEffect(() => {
    getSnappedNudgeItemsRef.current = getSnappedNudgeItems
  }, [getSnappedNudgeItems, getSnappedNudgeItemsRef])

  return { getGuideSnappedMoveDelta, getSnappedNudgeItems }
}

export type GestureSnappingApi = ReturnType<typeof useGestureSnapping>
